import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import {
  ModifierPrompt,
  ModifierPromptRequest,
  PosManagerSharedObjects,
  PosQuantityRestriction,
  POSProductQuantityRestrictionsRequest,
  POSProductQuantityRestrictionsResponse,
  SetPriceBookPayload,
  SetPriceBookResponse,
  ProductionAreaOverride,
  GetPosEndOfDayTicketsResponse,
  GetEndOfDaysCountsResponse,
  GetPosEndOfDayStatus,
  VoidOrCloseAllTicketsResponse,
  RealtimeReportParams,
  PopularItemsResponse,
  SalesByMenuCategoryResponse,
  SalesByPosOperatorsResponse,
  TotalsResponse,
  SalesByPaymentTypeResponse,
  SalesByDinerTypeResponse
} from 'src/app/models/pos-manager.model';
import {
  ReportRecipientResponse,
  SendPdfReportRequest,
  SendPdfReportResponse,
  PrintReportRequest,
  PrintReportResponse
} from 'src/app/models/pos-reports-api.model';
import { ReportData } from 'src/app/models/sale-report.model';
import { TranslocoService } from '@ngneat/transloco';
import { ApiType } from 'src/app/pos.config';
import { ErrorService } from 'src/app/services/error/error.service';
import { AppStateHelper } from 'src/app/store/app/app.state.helper';
import { APP_STATE_TOKEN } from 'src/app/store/app/app.state.model';
import { LOCATION_STATE_TOKEN } from 'src/app/store/location/location.state';

@Injectable({
  providedIn: 'root'
})
export class PosManagerService {
  constructor(
    private http: HttpClient,
    private store: Store,
    private readonly errorService: ErrorService,
    private translocoService: TranslocoService
  ) {}

  getSharedObjects(): Observable<PosManagerSharedObjects> {
    const url = `${this.getBaseUrl()}shared_objects`;
    return this.http.get<PosManagerSharedObjects>(url).pipe(
      this.errorService.retryPipe(ApiType.Query),
      map((response) => response)
    );
  }

  getModifierPrompts(): Observable<ModifierPrompt[]> {
    return this.http
      .get<{
        modifier_prompts: ModifierPrompt[];
      }>(`${this.getBaseUrl()}modifier_prompts`)
      .pipe(
        this.errorService.retryPipe(ApiType.Query),
        map((response) => response.modifier_prompts)
      );
  }

  createModifierPrompt(
    data: ModifierPromptRequest
  ): Observable<ModifierPrompt> {
    return this.http
      .post<ModifierPrompt>(`${this.getBaseUrl()}modifier_prompts`, data)
      .pipe(
        this.errorService.retryPipe(ApiType.Mutate),
        map((response) => response)
      );
  }

  updateModifierPrompt(
    id: number,
    data: ModifierPromptRequest
  ): Observable<ModifierPrompt> {
    return this.http
      .put<ModifierPrompt>(`${this.getBaseUrl()}modifier_prompts/${id}`, data)
      .pipe(
        this.errorService.retryPipe(ApiType.Mutate),
        map((response) => response)
      );
  }

  deleteModifierPrompt(id: number): Observable<boolean> {
    return this.http
      .delete<{
        success: boolean;
      }>(`${this.getBaseUrl()}modifier_prompts/${id}`)
      .pipe(
        this.errorService.retryPipe(ApiType.Mutate),
        map((response) => response.success)
      );
  }

  setPriceBook({
    priceBookId,
    itemType,
    itemId,
    price_type,
    price
  }: SetPriceBookPayload): Observable<SetPriceBookResponse> {
    return this.http
      .post<SetPriceBookResponse>(
        this.getBaseUrl() + 'pos_price_books/' + priceBookId + '/set_price',
        {
          pos_price_book_item: {
            price_bookable_type: itemType,
            price_bookable_id: itemId,
            price_type,
            price
          }
        }
      )
      .pipe(this.errorService.retryPipe(ApiType.Mutate));
  }

  private getBaseUrl(): string {
    const baseUrl = this.store.selectSnapshot(APP_STATE_TOKEN).apiBaseUrl;
    const posManagerApiUrl = AppStateHelper.getPosManagerApiUrl(baseUrl);

    return posManagerApiUrl;
  }

  getQuantityRestrictions(date: string): Observable<PosQuantityRestriction[]> {
    return this.http
      .get<POSProductQuantityRestrictionsResponse>(
        `${this.getBaseUrl()}pos_quantity_restrictions`,
        { params: { date } }
      )
      .pipe(
        this.errorService.retryPipe(ApiType.Query),
        map((response) => response?.quantity_restrictions || []),
        catchError(() => of([]))
      );
  }

  createQuantityRestriction(
    data: POSProductQuantityRestrictionsRequest
  ): Observable<PosQuantityRestriction> {
    return this.http
      .post<PosQuantityRestriction>(
        `${this.getBaseUrl()}pos_quantity_restrictions`,
        data
      )
      .pipe(
        this.errorService.retryPipe(ApiType.Mutate),
        map((response) => response)
      );
  }

  updateQuantityRestriction(
    data: Partial<POSProductQuantityRestrictionsRequest>
  ): Observable<PosQuantityRestriction> {
    return this.http
      .put<PosQuantityRestriction>(
        `${this.getBaseUrl()}pos_quantity_restrictions/${data.id}`,
        data
      )
      .pipe(
        this.errorService.retryPipe(ApiType.Mutate),
        map((response) => response)
      );
  }

  deleteQuantityRestriction(restriction_id: number): Observable<boolean> {
    return this.http
      .delete<{
        success: boolean;
      }>(`${this.getBaseUrl()}pos_quantity_restrictions/${restriction_id}`)
      .pipe(
        this.errorService.retryPipe(ApiType.Mutate),
        map((response) => response.success)
      );
  }

  getEmbeddedDashboardUrl(): Observable<string> {
    return this.http
      .get<{ iframe_url: string }>(`${this.getBaseUrl()}pos_dashboard`)
      .pipe(
        this.errorService.retryPipe(ApiType.Query),
        map((res) => res.iframe_url)
      );
  }

  get canAccessReportingPage(): boolean {
    const { facility_details, operator } =
      this.store.selectSnapshot(APP_STATE_TOKEN);
    return (
      facility_details?.enable_pos_beta_reports &&
      !!operator.permissions.view_pos_beta_reports_in_pos_manager
    );
  }

  getProductionAreaOverrides(): Observable<ProductionAreaOverride[]> {
    return this.http
      .get<{
        overrides: ProductionAreaOverride[];
      }>(`${this.getBaseUrl()}production_area_overrides`)
      .pipe(
        this.errorService.retryPipe(ApiType.Query),
        map((response) => response.overrides)
      );
  }

  createOrUpdateProductionAreaOverride(
    data: ProductionAreaOverride,
    isAlreadyOverridden: boolean
  ): Observable<ProductionAreaOverride> {
    const url = `${this.getBaseUrl()}production_area_overrides`;
    const loadingParams = {
      params: {
        loadingText: this.translocoService.translate('messages.saving')
      }
    };

    const request = isAlreadyOverridden
      ? this.http.put<{ override: ProductionAreaOverride }>(
          url + `/${data.id}`,
          data,
          loadingParams
        )
      : this.http.post<{ override: ProductionAreaOverride }>(
          url,
          data,
          loadingParams
        );

    return request.pipe(
      this.errorService.retryPipe(ApiType.Mutate),
      map((response) => response.override)
    );
  }

  deleteProductionAreaOverrides(id: number): Observable<boolean> {
    return this.http
      .delete<{ success: boolean }>(
        `${this.getBaseUrl()}production_area_overrides/${id}`,
        {
          params: {
            loadingText: this.translocoService.translate('messages.saving')
          }
        }
      )
      .pipe(
        this.errorService.retryPipe(ApiType.Mutate),
        map((response) => response.success)
      );
  }

  getEndOfDayReport(params: {
    date: string;
    pos_location_id?: number;
    meal_id?: number;
    end_day_report?: boolean;
  }): Observable<ReportData> {
    return this.http
      .get<{ report_data: ReportData }>(
        `${this.getBaseUrl()}pos_tickets/sales_report`,
        {
          params
        }
      )
      .pipe(map(({ report_data }) => report_data));
  }

  getReportRecipients(): Observable<string[]> {
    const url = `${this.getBaseUrl()}pos_tickets/report_recipient`;

    return this.http
      .get<ReportRecipientResponse>(url, {
        params: {
          pos_location_id:
            this.store.selectSnapshot(LOCATION_STATE_TOKEN).location.id
        }
      })
      .pipe(
        this.errorService.retryPipe(ApiType.Query),
        map((response) => response.recipients)
      );
  }

  sendPdfReport(
    request: SendPdfReportRequest
  ): Observable<SendPdfReportResponse> {
    const url = `${this.getBaseUrl()}pos_tickets/send_pdf_report`;
    return this.http
      .post<SendPdfReportResponse>(url, request)
      .pipe(this.errorService.retryPipe(ApiType.Mutate));
  }

  printReport(request: PrintReportRequest): Observable<PrintReportResponse> {
    const url = `${this.getBaseUrl()}pos_tickets/print_report`;
    return this.http
      .post<PrintReportResponse>(url, request)
      .pipe(this.errorService.retryPipe(ApiType.Mutate));
  }

  getEndOfDaysCounts(params: {
    date: string;
    pos_location_id: number;
  }): Observable<GetEndOfDaysCountsResponse> {
    const apiUrl = this.getBaseUrl() + 'pos_tickets/end_of_day_counts';

    return this.http.get<GetEndOfDaysCountsResponse>(apiUrl, {
      params
    });
  }

  getEndOfDayPosTickets(params: {
    status: GetPosEndOfDayStatus;
    date: string;
    pos_location_id: number;
  }): Observable<GetPosEndOfDayTicketsResponse> {
    const apiUrl = this.getBaseUrl() + 'pos_tickets';

    return this.http
      .get<GetPosEndOfDayTicketsResponse>(apiUrl, { params })
      .pipe(this.errorService.retryPipe(ApiType.Query));
  }

  voidAllUnpaidEndOfDayTickets(body: {
    date: string;
    pos_location_id: number;
    void_operator_id: number;
    approving_void_operator_id: number;
  }): Observable<VoidOrCloseAllTicketsResponse> {
    const apiUrl = this.getBaseUrl() + 'pos_tickets/void_all_unpaid';

    return this.http.post<VoidOrCloseAllTicketsResponse>(apiUrl, body);
  }

  closeAllPaidTickets(body: {
    date: string;
    pos_location_id: number;
  }): Observable<VoidOrCloseAllTicketsResponse> {
    const apiUrl = this.getBaseUrl() + 'pos_tickets/close_all_paid';

    return this.http
      .post<VoidOrCloseAllTicketsResponse>(apiUrl, body)
      .pipe(map((response) => response));
  }

  getRealtimeTotals(params: RealtimeReportParams): Observable<TotalsResponse> {
    return this.http
      .get<TotalsResponse>(`${this.getReportsBaseUrl()}realtime_info/totals`, {
        params: params as any
      })
      .pipe(
        this.errorService.retryPipe(ApiType.Query),
        map((response) => response)
      );
  }

  getRealtimeSalesByPaymentType(
    params: RealtimeReportParams
  ): Observable<SalesByPaymentTypeResponse> {
    return this.http
      .get<SalesByPaymentTypeResponse>(
        `${this.getReportsBaseUrl()}realtime_info/sales_by_payment_type`,
        { params: params as any }
      )
      .pipe(
        this.errorService.retryPipe(ApiType.Query),
        map((response) => response)
      );
  }

  getRealtimeSalesByDinerType(
    params: RealtimeReportParams
  ): Observable<SalesByDinerTypeResponse> {
    return this.http
      .get<SalesByDinerTypeResponse>(
        `${this.getReportsBaseUrl()}realtime_info/sales_by_diner_type`,
        { params: params as any }
      )
      .pipe(
        this.errorService.retryPipe(ApiType.Query),
        map((response) => response)
      );
  }

  getRealtimeMostPopularItems(
    params: RealtimeReportParams
  ): Observable<PopularItemsResponse> {
    return this.http
      .get<PopularItemsResponse>(
        `${this.getReportsBaseUrl()}realtime_info/most_popular_items`,
        { params: params as any }
      )
      .pipe(
        this.errorService.retryPipe(ApiType.Query),
        map((response) => response)
      );
  }

  getRealtimeSalesByMenuCategory(
    params: RealtimeReportParams
  ): Observable<SalesByMenuCategoryResponse> {
    return this.http
      .get<SalesByMenuCategoryResponse>(
        `${this.getReportsBaseUrl()}realtime_info/sales_by_menu_category`,
        { params: params as any }
      )
      .pipe(
        this.errorService.retryPipe(ApiType.Query),
        map((response) => response)
      );
  }

  getRealtimeSalesByPosOperators(
    params: RealtimeReportParams
  ): Observable<SalesByPosOperatorsResponse> {
    return this.http
      .get<SalesByPosOperatorsResponse>(
        `${this.getReportsBaseUrl()}realtime_info/sales_by_pos_operators`,
        { params: params as any }
      )
      .pipe(
        this.errorService.retryPipe(ApiType.Query),
        map((response) => response)
      );
  }

  private getReportsBaseUrl(): string {
    const baseUrl = this.store.selectSnapshot(APP_STATE_TOKEN).apiBaseUrl;
    return `${baseUrl}/api/pos/v3/reports/`;
  }
}
